import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiPlus, FiEdit, FiTrash2, FiMoreVertical, FiCheck, FiX,
  FiUsers, FiShield, FiSettings, FiAlertCircle, FiSliders, FiCloud
} from 'react-icons/fi';
import DashboardLayout from '../Dashboard/DashboardLayout';
import * as environmentsApi from '../../api/environments';
import { getConnections, ConnectionService } from '../../api/connections';
import EnvironmentApproversModal from './EnvironmentApproversModal';

interface Environment {
  id: string;
  name: string;
  displayName?: string;
  type: 'dev' | 'staging' | 'uat' | 'preprod' | 'prod' | 'dr' | 'custom';
  description?: string;
  color?: string;
  icon?: string;
  sortOrder?: number;
  autoApprove?: boolean;
  requiresApproval?: boolean;
  approvalRequiredCount?: number;
  isProtected?: boolean;
  allowDestroy?: boolean;
  connection_service_id?: string | null;
  connection_service_name?: string | null;
  connection_service_provider?: string | null;
  created_at: string;
  updated_at?: string;
}

interface EnvironmentQuota {
  plan: 'FREE' | 'STANDARD' | 'ENTERPRISE';
  used: number;
  limit: number | string;
  remaining: number | string;
  canCreate: boolean;
}

const EnvironmentsPage: React.FC = () => {
  const navigate = useNavigate();
  const [environments, setEnvironments] = useState<Environment[]>([]);
  const [quota, setQuota] = useState<EnvironmentQuota | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; right: number } | null>(null);
  const [connections, setConnections] = useState<ConnectionService[]>([]);

  // Form state pour création/édition
  const [formData, setFormData] = useState({
    name: '',
    displayName: '',
    type: 'dev' as Environment['type'],
    description: '',
    color: '#10B981',
    requiresApproval: false,
    autoApprove: false,
    isProtected: false,
    allowDestroy: true,
    connectionServiceId: null as string | null
  });

  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; envId: string | null; envName: string }>({
    isOpen: false,
    envId: null,
    envName: ''
  });
  const [approversModal, setApproversModal] = useState<{ isOpen: boolean; env: Environment | null }>({
    isOpen: false,
    env: null
  });

  useEffect(() => {
    loadEnvironments();
    loadQuota();
  }, []);

  useEffect(() => {
    const handleClickOutside = () => {
      if (activeMenu) {
        setActiveMenu(null);
        setMenuPosition(null);
      }
    };

    if (activeMenu) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [activeMenu]);

  // Charger les connexions cloud quand le modal s'ouvre
  useEffect(() => {
    if (showCreateModal && connections.length === 0) {
      getConnections()
        .then(setConnections)
        .catch(err => console.error('Erreur chargement connexions:', err));
    }
  }, [showCreateModal]);

  const loadEnvironments = async () => {
    try {
      const envs = await environmentsApi.listEnvironments();
      // Trier par sortOrder
      const sorted = envs.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
      setEnvironments(sorted);
    } catch (error: any) {
      console.error('Erreur lors du chargement des environnements:', error);
      alert('Erreur lors du chargement des environnements');
    } finally {
      setLoading(false);
    }
  };

  const loadQuota = async () => {
    try {
      const quotaData = await environmentsApi.getEnvironmentQuota();
      setQuota(quotaData);
    } catch (error: any) {
      console.error('Erreur lors du chargement des quotas:', error);
    }
  };

  const handleCreateEnvironment = async () => {
    try {
      await environmentsApi.createEnvironment(formData);
      await loadEnvironments();
      await loadQuota();
      setShowCreateModal(false);
      resetForm();
      alert('✅ Environnement créé avec succès !');
    } catch (error: any) {
      console.error('Erreur lors de la création:', error);
      const errorMsg = error.response?.data?.error || error.message;
      alert(`❌ Erreur: ${errorMsg}`);
    }
  };

  const handleUpdateEnvironment = async () => {
    if (!editingId) return;

    try {
      await environmentsApi.updateEnvironment(editingId, formData);
      await loadEnvironments();
      setShowCreateModal(false);
      setEditingId(null);
      resetForm();
      alert('✅ Environnement mis à jour avec succès !');
    } catch (error: any) {
      console.error('Erreur lors de la mise à jour:', error);
      const errorMsg = error.response?.data?.error || error.message;
      alert(`❌ Erreur: ${errorMsg}`);
    }
  };

  const handleDeleteEnvironment = async () => {
    if (!deleteConfirm.envId) return;

    try {
      await environmentsApi.deleteEnvironment(deleteConfirm.envId);
      await loadEnvironments();
      await loadQuota();
      setDeleteConfirm({ isOpen: false, envId: null, envName: '' });
      alert('✅ Environnement supprimé avec succès !');
    } catch (error: any) {
      console.error('Erreur lors de la suppression:', error);
      const errorMsg = error.response?.data?.error || error.message;
      alert(`❌ Erreur: ${errorMsg}`);
    }
  };

  const openEditModal = (env: Environment) => {
    setEditingId(env.id);
    setFormData({
      name: env.name,
      displayName: env.displayName || '',
      type: env.type,
      description: env.description || '',
      color: env.color || '#10B981',
      requiresApproval: env.requiresApproval || false,
      autoApprove: env.autoApprove || false,
      isProtected: env.isProtected || false,
      allowDestroy: env.allowDestroy !== undefined ? env.allowDestroy : true,
      connectionServiceId: env.connection_service_id || null
    });
    setShowCreateModal(true);
    setActiveMenu(null);
  };

  const openDeleteConfirm = (env: Environment) => {
    setDeleteConfirm({
      isOpen: true,
      envId: env.id,
      envName: env.displayName || env.name
    });
    setActiveMenu(null);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      displayName: '',
      type: 'dev',
      description: '',
      color: '#10B981',
      requiresApproval: false,
      autoApprove: false,
      isProtected: false,
      allowDestroy: true,
      connectionServiceId: null
    });
    setEditingId(null);
  };

  const getTypeIcon = (type: string) => {
    const icons: Record<string, string> = {
      dev: '🧪',
      staging: '📋',
      uat: '✅',
      preprod: '🛡️',
      prod: '🚀',
      dr: '🔄',
      custom: '⚙️'
    };
    return icons[type] || '📦';
  };

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      dev: 'Development',
      staging: 'Staging',
      uat: 'UAT',
      preprod: 'Pre-Production',
      prod: 'Production',
      dr: 'Disaster Recovery',
      custom: 'Custom'
    };
    return labels[type] || type;
  };

  const getProviderLabel = (provider: string) => {
    const labels: Record<string, string> = {
      azure: 'Azure',
      aws: 'AWS',
      gcp: 'GCP',
      azuredevops: 'Azure DevOps'
    };
    return labels[provider] || provider;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-8 flex items-center justify-center">
          <div className="text-gray-500">Chargement...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Environnements</h1>
              <p className="text-gray-600 mt-1">
                Gérez les environnements de déploiement de votre organisation
              </p>
            </div>

            <button
              onClick={() => {
                resetForm();
                setShowCreateModal(true);
              }}
              disabled={quota?.canCreate === false}
              className="flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
              title={quota?.canCreate === false ? 'Quota atteint' : 'Créer un environnement'}
            >
              <FiPlus size={20} />
              <span className="font-medium">Nouvel Environnement</span>
            </button>
          </div>

          {/* Quota Badge */}
          {quota && (
            <div className="mt-4 inline-flex items-center gap-3 bg-blue-50 border border-blue-200 px-4 py-2 rounded-lg">
              <FiShield className="text-blue-600" size={18} />
              <span className="text-sm font-medium text-blue-900">
                Plan {quota.plan}
              </span>
              <span className="text-sm text-blue-700">
                {quota.used} / {quota.limit === 'unlimited' ? '∞' : quota.limit} environnements utilisés
              </span>
              {quota.canCreate === false && (
                <span className="ml-2 text-xs bg-red-100 text-red-800 px-2 py-1 rounded">
                  Quota atteint
                </span>
              )}
            </div>
          )}
        </div>

        {/* Environments List */}
        {environments.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-xl border border-gray-200">
            <FiSettings size={64} className="mx-auto text-gray-300 mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              Aucun environnement
            </h3>
            <p className="text-gray-500 mb-6">
              Créez votre premier environnement pour commencer
            </p>
            <button
              onClick={() => {
                resetForm();
                setShowCreateModal(true);
              }}
              disabled={quota?.canCreate === false}
              className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
            >
              <FiPlus size={20} />
              Créer un environnement
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {environments.map((env) => (
              <div
                key={env.id}
                className="bg-white rounded-xl shadow-md border border-gray-200 p-6 hover:shadow-lg transition"
                style={{ borderTopColor: env.color, borderTopWidth: '4px' }}
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-12 h-12 rounded-lg flex items-center justify-center text-2xl"
                      style={{ backgroundColor: `${env.color}20` }}
                    >
                      {getTypeIcon(env.type)}
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900">{env.displayName || env.name}</h3>
                      <p className="text-sm text-gray-500">{getTypeLabel(env.type)}</p>
                      {env.connection_service_name ? (
                        <span className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full mt-1">
                          <FiCloud size={10} />
                          {env.connection_service_name}
                          {env.connection_service_provider && (
                            <span className="text-blue-400">· {getProviderLabel(env.connection_service_provider)}</span>
                          )}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-gray-400 mt-1">
                          <FiCloud size={10} />
                          Connexion par défaut
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="relative">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const rect = e.currentTarget.getBoundingClientRect();
                        if (activeMenu === env.id) {
                          setActiveMenu(null);
                          setMenuPosition(null);
                        } else {
                          setActiveMenu(env.id);
                          setMenuPosition({
                            top: rect.top,
                            right: window.innerWidth - rect.right
                          });
                        }
                      }}
                      className="p-2 hover:bg-gray-100 rounded-full transition"
                    >
                      <FiMoreVertical className="text-gray-600" />
                    </button>

                    {activeMenu === env.id && menuPosition && (
                      <div
                        className="fixed w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-[9999]"
                        style={{
                          top: `${menuPosition.top}px`,
                          right: `${menuPosition.right}px`
                        }}
                      >
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditModal(env);
                          }}
                          className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                        >
                          <FiEdit size={16} />
                          Modifier
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setApproversModal({ isOpen: true, env });
                            setActiveMenu(null);
                          }}
                          className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                        >
                          <FiUsers size={16} />
                          Approbateurs
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/environments/${env.id}/variables`);
                            setActiveMenu(null);
                          }}
                          className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                        >
                          <FiSliders size={16} />
                          Variables
                        </button>
                        <hr className="my-2" />
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openDeleteConfirm(env);
                          }}
                          disabled={env.isProtected}
                          className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <FiTrash2 size={16} />
                          Supprimer
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {env.description && (
                  <p className="text-sm text-gray-600 mb-4">{env.description}</p>
                )}

                <div className="flex flex-wrap gap-2 mb-4">
                  {env.requiresApproval && (
                    <span className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded-full flex items-center gap-1">
                      <FiShield size={12} />
                      Approbation requise
                    </span>
                  )}
                  {env.autoApprove && (
                    <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full flex items-center gap-1">
                      <FiCheck size={12} />
                      Auto-approuvé
                    </span>
                  )}
                  {env.isProtected && (
                    <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full flex items-center gap-1">
                      <FiShield size={12} />
                      Protégé
                    </span>
                  )}
                  {!env.allowDestroy && (
                    <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full flex items-center gap-1">
                      <FiAlertCircle size={12} />
                      Destroy désactivé
                    </span>
                  )}
                </div>

                <div className="text-xs text-gray-500">
                  Créé le {formatDate(env.created_at)}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create/Edit Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
              <h2 className="text-xl font-bold text-gray-900 mb-6">
                {editingId ? 'Modifier l\'environnement' : 'Créer un environnement'}
              </h2>

              <div className="space-y-4">
                {/* Name (slug) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom (slug) *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                    placeholder="dev, staging, prod-eu"
                    disabled={!!editingId}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white disabled:bg-gray-100"
                  />
                  <p className="text-xs text-gray-500 mt-1">Minuscules, chiffres et tirets uniquement</p>
                </div>

                {/* Display Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom affiché
                  </label>
                  <input
                    type="text"
                    value={formData.displayName}
                    onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                    placeholder="Development, Production Europe"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                  />
                </div>

                {/* Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Type *
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value as Environment['type'] })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                  >
                    <option value="dev">🧪 Development</option>
                    <option value="staging">📋 Staging</option>
                    <option value="uat">✅ UAT</option>
                    <option value="preprod">🛡️ Pre-Production</option>
                    <option value="prod">🚀 Production</option>
                    <option value="dr">🔄 Disaster Recovery</option>
                    <option value="custom">⚙️ Custom</option>
                  </select>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Description de l'environnement"
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                  />
                </div>

                {/* Color */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Couleur
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={formData.color}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                      className="w-16 h-10 rounded border border-gray-300 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={formData.color}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                      placeholder="#10B981"
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                    />
                  </div>
                </div>

                {/* Connexion cloud */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Connexion cloud
                  </label>
                  <select
                    value={formData.connectionServiceId || ''}
                    onChange={(e) => setFormData({ ...formData, connectionServiceId: e.target.value || null })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                  >
                    <option value="">(Par défaut — connexion active)</option>
                    {connections.map((conn) => (
                      <option key={conn.id} value={conn.id}>
                        {conn.name} · {getProviderLabel(conn.provider)}{!conn.is_active ? ' (inactive)' : ''}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Connexion utilisée pour les déploiements dans cet environnement. Vide = première connexion active.
                  </p>
                </div>

                {/* Options */}
                <div className="space-y-3 border-t pt-4">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.requiresApproval}
                      onChange={(e) => setFormData({ ...formData, requiresApproval: e.target.checked })}
                      className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-900">Nécessite une approbation</span>
                      <p className="text-xs text-gray-500">Les déploiements nécessitent l'approbation d'un administrateur</p>
                    </div>
                  </label>

                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.autoApprove}
                      onChange={(e) => setFormData({ ...formData, autoApprove: e.target.checked })}
                      className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-900">Auto-approuver</span>
                      <p className="text-xs text-gray-500">Les déploiements sont approuvés automatiquement</p>
                    </div>
                  </label>

                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.isProtected}
                      onChange={(e) => setFormData({ ...formData, isProtected: e.target.checked })}
                      className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-900">Protéger contre la suppression</span>
                      <p className="text-xs text-gray-500">Empêche la suppression accidentelle de l'environnement</p>
                    </div>
                  </label>

                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.allowDestroy}
                      onChange={(e) => setFormData({ ...formData, allowDestroy: e.target.checked })}
                      className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-900">Autoriser Terraform Destroy</span>
                      <p className="text-xs text-gray-500">Permet d'exécuter terraform destroy sur cet environnement</p>
                    </div>
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
                >
                  Annuler
                </button>
                <button
                  onClick={editingId ? handleUpdateEnvironment : handleCreateEnvironment}
                  disabled={!formData.name || !formData.type}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {editingId ? 'Mettre à jour' : 'Créer'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteConfirm.isOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Supprimer l'environnement</h2>
              <p className="text-gray-600 mb-6">
                Êtes-vous sûr de vouloir supprimer l'environnement <strong>{deleteConfirm.envName}</strong> ?
                Cette action est irréversible.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setDeleteConfirm({ isOpen: false, envId: null, envName: '' })}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
                >
                  Annuler
                </button>
                <button
                  onClick={handleDeleteEnvironment}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                >
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Approvers Modal */}
        {approversModal.isOpen && approversModal.env && (
          <EnvironmentApproversModal
            envId={approversModal.env.id}
            envName={approversModal.env.displayName || approversModal.env.name}
            requiresApproval={approversModal.env.requiresApproval ?? false}
            autoApprove={approversModal.env.autoApprove ?? false}
            approvalRequiredCount={approversModal.env.approvalRequiredCount ?? 1}
            onClose={() => setApproversModal({ isOpen: false, env: null })}
            onSaved={() => loadEnvironments()}
          />
        )}

      </div>
    </DashboardLayout>
  );
};

export default EnvironmentsPage;
